import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTradesFilterStore, useTradesDataStore, useTradesColumnStore, useUIStore, useCurrencyDisplayStore } from '@/stores';
import { tradesService } from '@/services/trades/trades-service';
import { MultiSelectDropdown } from '@/components/ui/MultiSelectDropdown';
import { BaseFilterSidebar } from '@/components/ui/BaseFilterSidebar';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { CollapsibleFilterSection } from '@/features/inventory/components/CollapsibleFilterSection';
import { TradesColumnManagerPanel } from './TradesColumnManagerPanel';
import { NumericRangeFilter } from './NumericRangeFilter';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { exportTradesToCSV } from '@/utils/trades/csv-export';
import { applyLocalTradeFilters } from '@/utils/trades/apply-trade-filters';
import { TRADES_FILTER_ID_TO_COLUMN_ID } from '@/utils/trades/filter-column-mapping';
import { clsx } from 'clsx';
import { SavedViewSelector } from '@/features/saved-views';
import { tradesSavedViewsController, getTradesSavedViewsHooks } from '@/features/trades/saved-views';
import { sortFiltersByColumnOrder as sortFilters } from '@/utils/filter-utils';
import { COLLATERAL_TYPE_LABELS, TRADE_STATUS_LABELS, transformPositionToTradeItem } from '@/types';
import { counterpartyOptionLabel, useCounterpartySearch } from '@/hooks/counterparty';
import {
  TRADE_AGREEMENT_TYPE_OPTIONS,
  TRADE_COLLATERAL_TYPE_OPTIONS,
  TRADE_CURRENCY_OPTIONS,
  NON_TERMINAL_TRADE_STATUS_OPTIONS,
  TERMINAL_TRADE_STATUS_OPTIONS,
  TRADE_TERM_TYPE_OPTIONS,
  TRADE_TYPE_OPTIONS,
} from '@/features/trades/constants/filter-options';

interface FilterSidebarProps {
  filteredDataCount?: number;
  isDataLoading?: boolean;
  hasError?: boolean;
  /** LR-497/LR-376: History shows terminal positions, All Trades the rest — so
   *  the Status filter offers only the statuses reachable in the current view. */
  isHistoryMode?: boolean;
  /** LR-671: saved-views selector in the header (parity with Inventory). */
  currentUserEmail?: string | null;
  /** Fires when the active saved view changes inside the selector; Layout wires
   *  it to `navigate({ search: { view_id } })` so the URL stays the source of truth. */
  onActiveViewChange?: (viewId: number | null) => void;
}

type SidebarTab = 'filters' | 'columns';

type FilterDef = {
  id: string;
  label: string;
};

const sortFiltersByColumnOrder = (filters: FilterDef[], columns: { id: string }[]) =>
  sortFilters(filters, columns, TRADES_FILTER_ID_TO_COLUMN_ID);

const MOCK_TRADE_TYPES = TRADE_TYPE_OPTIONS;
const MOCK_COLLATERAL_TYPES = TRADE_COLLATERAL_TYPE_OPTIONS;
const MOCK_AGREEMENT_TYPES = TRADE_AGREEMENT_TYPE_OPTIONS;
const MOCK_TERM_TYPES = TRADE_TERM_TYPE_OPTIONS;
const MOCK_CURRENCIES = TRADE_CURRENCY_OPTIONS;

/**
 * The active counterparties while the box is empty, the whole registry (deactivated
 * included) once a query is typed, both paged from the server. Values stay names: the
 * filter store and saved views persist names, and TradesPage resolves them to ids.
 */
const CounterpartyFilter: React.FC<{
  selected: string[];
  onChange: (values: string[]) => void;
}> = ({ selected, onChange }) => {
  const [query, setQuery] = useState('');
  const searching = query !== '';
  const active = useCounterpartySearch('', true, { enabled: !searching });
  const search = useCounterpartySearch(query, false, { enabled: searching });
  const list = searching ? search : active;
  // Labels of rows seen in this session, so a selected deactivated name keeps its
  // "(inactive)" while it sits pinned after the query that found it is gone.
  const seenLabels = useRef<Record<string, string>>({});

  const options = useMemo(() => {
    const names = new Set<string>();
    for (const page of list.data?.pages ?? []) {
      for (const cp of page.results) {
        if (!cp.name?.trim()) continue;
        names.add(cp.name);
        seenLabels.current[cp.name] = counterpartyOptionLabel(cp.name, cp.isActive);
      }
    }
    return [...names];
  }, [list.data]);

  return (
    <MultiSelectDropdown
      options={options}
      selectedValues={selected}
      onChange={onChange}
      placeholder="Select counterparties..."
      searchPlaceholder="Search counterparties..."
      pinSelectedToTop={true}
      showClearAll={false}
      optionLabels={seenLabels.current}
      onSearchChange={setQuery}
      isLoading={list.isLoading}
      isError={list.isError}
      hasMore={list.hasNextPage}
      onLoadMore={() => void list.fetchNextPage()}
      isLoadingMore={list.isFetchingNextPage}
      loadMoreFailed={list.isFetchNextPageError}
    />
  );
};

/** Filter order matches table column order (left-to-right). */
const FILTER_DEFS: FilterDef[] = [
  { id: 'statuses', label: 'Status' },
  { id: 'counterparties', label: 'Counterparty' },
  { id: 'symbols', label: 'Security' },
  { id: 'agreementTypes', label: 'Agreement Type' },
  { id: 'tradeTypes', label: 'Trade Type' },
  { id: 'collateralTypes', label: 'Collateral Type' },
  { id: 'termTypes', label: 'Trade Term' },
  { id: 'quantity', label: 'Quantity' },
  { id: 'grossPrice', label: 'Trade Gross Price' },
  { id: 'contractValue', label: 'Trade Margin Value' },
  { id: 'margin', label: 'Margin %' },
  // LR-474: '(USD)' dropped — the column carries per-row currencies (LR-459
  // renamed only the table header; the sidebar label kept lying USD).
  { id: 'dailyEarnings', label: 'Daily Earnings' },
  { id: 'tradeDate', label: 'Trade Date' },
  { id: 'settlementDate', label: 'Settlement Date' },
  { id: 'maturityDate', label: 'Maturity Date' },
  // LR-474 (D3): filters the SECURITY (quotation) currency; the settlement-ccy
  // filter lives in the summary-bar currency pill, not the sidebar.
  { id: 'currencies', label: 'Security Currency' },
  { id: 'createdAt', label: 'Created At' },
  { id: 'updatedAt', label: 'Updated At' },
];

export const FilterSidebar: React.FC<FilterSidebarProps> = ({
  isHistoryMode = false,
  currentUserEmail = null,
  onActiveViewChange,
}) => {
  // LR-497/LR-376: the Status filter offers only the statuses reachable in the
  // current view — terminal (Closed/Cancelled) in History, the rest in All Trades
  // — so it never lists dead options that filter to an empty list.
  const statusOptions = isHistoryMode ? TERMINAL_TRADE_STATUS_OPTIONS : NON_TERMINAL_TRADE_STATUS_OPTIONS;
  const localTradesFilters = useTradesFilterStore((state) => state.localTradesFilters);
  const setLocalTradesFilters = useTradesFilterStore((state) => state.setLocalTradesFilters);
  const expandedFilterIds = useUIStore((state) => state.expandedFilterIds);
  const expandFilter = useUIStore((state) => state.expandFilter);
  const collapseFilter = useUIStore((state) => state.collapseFilter);
  const expandAllFilters = useUIStore((state) => state.expandAllFilters);
  const collapseAllFilters = useUIStore((state) => state.collapseAllFilters);
  const focusedFilterId = useUIStore((state) => state.focusedFilterId);
  const setFocusedFilterId = useUIStore((state) => state.setFocusedFilterId);
  const filterSidebarCollapsed = useUIStore((state) => state.filterSidebarCollapsed);
  const activeTab = useUIStore((s) => s.sidebarActiveTab) as SidebarTab;
  const setSidebarActiveTab = useUIStore((s) => s.setSidebarActiveTab);
  const setColumnsTabActive = useUIStore((state) => state.setColumnsTabActive);

  const filteredData = useTradesDataStore((state) => state.filteredTradesData);
  const viewScopedTradesData = useTradesDataStore((state) => state.viewScopedTradesData);
  // LR-546: the list scope + server total, so the CSV export fetches the WHOLE
  // filtered book instead of the loaded window and labels the true row count.
  const tradesListParams = useTradesDataStore((state) => state.tradesListParams);
  const tradesTotalCount = useTradesDataStore((state) => state.tradesTotalCount);
  // Whole-book option pools published by TradesPage (server aggregate), preferred
  // over the window-derived lists below; empty until loaded → window fallback.
  const storeSymbolOptions = useTradesDataStore((state) => state.symbolOptions);
  const storeCurrencyOptions = useTradesDataStore((state) => state.currencyOptions);
  const columnConfig = useTradesColumnStore((state) => state.columns);
  const setLocateColumnRequest = useUIStore((state) => state.setLocateColumnRequest);

  const securityOptions = useMemo(() => {
    if (storeSymbolOptions && storeSymbolOptions.length > 0) return storeSymbolOptions;
    const src = viewScopedTradesData && viewScopedTradesData.length > 0 ? viewScopedTradesData : filteredData;
    return Array.from(new Set(src.map((t) => t.symbol_id).filter(Boolean))).sort();
  }, [storeSymbolOptions, viewScopedTradesData, filteredData]);

  const currencyOptions = useMemo(() => {
    if (storeCurrencyOptions && storeCurrencyOptions.length > 0) return storeCurrencyOptions;
    const src = viewScopedTradesData && viewScopedTradesData.length > 0 ? viewScopedTradesData : filteredData;
    const live = Array.from(
      new Set(src.map((t) => (t.currency || '').toUpperCase()).filter(Boolean)),
    ).sort();
    return live.length > 0 ? live : MOCK_CURRENCIES;
  }, [storeCurrencyOptions, viewScopedTradesData, filteredData]);

  const handleTabChange = useCallback((tab: SidebarTab) => {
    setSidebarActiveTab(tab);
    setColumnsTabActive(tab === 'columns');
  }, [setSidebarActiveTab, setColumnsTabActive]);

  const baseCurrency = useCurrencyDisplayStore((s) => s.baseCurrency);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<{ done: number; total: number } | null>(null);
  // Daily Earnings is the ONLY sidebar predicate the server can't express (map-server-
  // params omits it — it's a rate-engine property), so under it the server
  // `tradesTotalCount` over-states the export and the exact whole-book count is unknown
  // until the fetch. Mirror TradesPage's `hasClientOnlyFilter` (kvi-r2 #5) → don't
  // promise a precise number in the label.
  const hasClientOnlyFilter =
    localTradesFilters.dailyEarningsMin !== null || localTradesFilters.dailyEarningsMax !== null;
  // Nor while the scope is published but its total is not: a new selection still loading.
  const exportCountUnknown = hasClientOnlyFilter || (tradesListParams != null && tradesTotalCount === undefined);
  const exportRowCount = tradesTotalCount ?? filteredData.length;
  const exportProgressText = exportProgress
    ? `${exportProgress.done.toLocaleString('en-US')} of ${exportProgress.total.toLocaleString('en-US')} rows fetched`
    : null;
  const handleExport = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      // The blotter is windowed, so `filteredData` is only the loaded page — fetch the
      // WHOLE filtered book (same server params as the list) so the export isn't silently
      // truncated (LR-546). Then REAPPLY `applyLocalTradeFilters`: the visible grid runs
      // that client pass over the server window (Daily Earnings has no server filter), so
      // the export must run it too or it leaks rows the trader filtered out AND totals them
      // (kvi-r6 #1). For every server-expressible predicate the pass is idempotent (the
      // server already applied it), so this keeps the export byte-identical to the grid's
      // predicate. Fall back to the already-client-filtered window only before params
      // publish (first paint).
      const rows = tradesListParams
        ? applyLocalTradeFilters(
            (
              await tradesService.getAllTrades(tradesListParams, {
                onProgress: (done, total) => setExportProgress({ done, total }),
              })
            ).map(transformPositionToTradeItem),
            localTradesFilters,
          )
        : filteredData;
      exportTradesToCSV(rows, columnConfig, baseCurrency);
    } catch (err) {
      // Keep alert() — the established export/download pattern (availability sidebar,
      // csv-export) — but surface the cause so a failed export is diagnosable (kvi-r6 #3).
      console.error('Trades CSV export failed:', err);
      alert('Failed to export trades. Please try again.');
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  }, [isExporting, tradesListParams, localTradesFilters, filteredData, columnConfig, baseCurrency]);

  // Auto-clear focus after 2s
  useEffect(() => {
    if (focusedFilterId == null) return;
    const t = setTimeout(() => setFocusedFilterId(null), 2000);
    return () => clearTimeout(t);
  }, [focusedFilterId, setFocusedFilterId]);

  const updateFilter = useCallback((key: keyof typeof localTradesFilters, value: unknown) => {
    setLocalTradesFilters({
      ...localTradesFilters,
      [key]: value,
    });
  }, [localTradesFilters, setLocalTradesFilters]);

  const clearAllFilters = useCallback(() => {
    setLocalTradesFilters({
      statuses: [],
      counterparties: [],
      symbols: [],
      tradeTypes: [],
      collateralTypes: [],
      agreementTypes: [],
      termTypes: [],
      legalEntities: [],
      accountIds: [],
      currencies: [],
      // LR-474 r1 #2 (D3): the sidebar's Clear All must NOT wipe the pill's
      // settlement-ccy filter — the sidebar badge never counts it, and the pill
      // has its own Select all. Preserve the current selection.
      settlementCurrencies: localTradesFilters.settlementCurrencies,
      tradeDateFrom: null,
      tradeDateTo: null,
      settlementDateFrom: null,
      settlementDateTo: null,
      maturityDateFrom: null,
      maturityDateTo: null,
      quantityMin: null,
      quantityMax: null,
      contractValueMin: null,
      contractValueMax: null,
      marginMin: null,
      marginMax: null,
      grossPriceMin: null,
      grossPriceMax: null,
      dailyEarningsMin: null,
      dailyEarningsMax: null,
      createdAtFrom: null,
      createdAtTo: null,
      updatedAtFrom: null,
      updatedAtTo: null,
      rateMin: null,
      rateMax: null,
    });
  }, [setLocalTradesFilters, localTradesFilters.settlementCurrencies]);

  const hasActiveFilter = useCallback((filterId: string): boolean => {
    switch (filterId) {
      case 'statuses': return localTradesFilters.statuses.length > 0;
      case 'counterparties': return localTradesFilters.counterparties.length > 0;
      case 'symbols': return localTradesFilters.symbols.length > 0;
      case 'tradeTypes': return localTradesFilters.tradeTypes.length > 0;
      case 'collateralTypes': return localTradesFilters.collateralTypes.length > 0;
      case 'agreementTypes': return localTradesFilters.agreementTypes.length > 0;
      case 'termTypes': return localTradesFilters.termTypes.length > 0;
      case 'currencies': return localTradesFilters.currencies.length > 0;
      case 'tradeDate': return !!(localTradesFilters.tradeDateFrom || localTradesFilters.tradeDateTo);
      case 'settlementDate': return !!(localTradesFilters.settlementDateFrom || localTradesFilters.settlementDateTo);
      case 'maturityDate': return !!(localTradesFilters.maturityDateFrom || localTradesFilters.maturityDateTo);
      case 'quantity': return localTradesFilters.quantityMin !== null || localTradesFilters.quantityMax !== null;
      case 'contractValue': return localTradesFilters.contractValueMin !== null || localTradesFilters.contractValueMax !== null;
      case 'margin': return localTradesFilters.marginMin !== null || localTradesFilters.marginMax !== null;
      case 'grossPrice': return localTradesFilters.grossPriceMin !== null || localTradesFilters.grossPriceMax !== null;
      case 'dailyEarnings': return localTradesFilters.dailyEarningsMin !== null || localTradesFilters.dailyEarningsMax !== null;
      case 'createdAt': return !!(localTradesFilters.createdAtFrom || localTradesFilters.createdAtTo);
      case 'updatedAt': return !!(localTradesFilters.updatedAtFrom || localTradesFilters.updatedAtTo);
      default: return false;
    }
  }, [localTradesFilters]);

  const activeFilterCount =
    (localTradesFilters.statuses.length > 0 ? 1 : 0) +
    (localTradesFilters.counterparties.length > 0 ? 1 : 0) +
    (localTradesFilters.symbols.length > 0 ? 1 : 0) +
    (localTradesFilters.tradeTypes.length > 0 ? 1 : 0) +
    (localTradesFilters.collateralTypes.length > 0 ? 1 : 0) +
    (localTradesFilters.agreementTypes.length > 0 ? 1 : 0) +
    (localTradesFilters.termTypes.length > 0 ? 1 : 0) +
    (localTradesFilters.currencies.length > 0 ? 1 : 0) +
    (localTradesFilters.tradeDateFrom || localTradesFilters.tradeDateTo ? 1 : 0) +
    (localTradesFilters.settlementDateFrom || localTradesFilters.settlementDateTo ? 1 : 0) +
    (localTradesFilters.maturityDateFrom || localTradesFilters.maturityDateTo ? 1 : 0) +
    (localTradesFilters.quantityMin !== null || localTradesFilters.quantityMax !== null ? 1 : 0) +
    (localTradesFilters.contractValueMin !== null || localTradesFilters.contractValueMax !== null ? 1 : 0) +
    (localTradesFilters.marginMin !== null || localTradesFilters.marginMax !== null ? 1 : 0) +
    (localTradesFilters.grossPriceMin !== null || localTradesFilters.grossPriceMax !== null ? 1 : 0) +
    (localTradesFilters.dailyEarningsMin !== null || localTradesFilters.dailyEarningsMax !== null ? 1 : 0) +
    (localTradesFilters.createdAtFrom || localTradesFilters.createdAtTo ? 1 : 0) +
    (localTradesFilters.updatedAtFrom || localTradesFilters.updatedAtTo ? 1 : 0) +
    (localTradesFilters.rateMin !== null || localTradesFilters.rateMax !== null ? 1 : 0);

  const renderMultiSelect = (
    filterId: string,
    filterKey: keyof typeof localTradesFilters,
    options: readonly string[],
    placeholder: string,
    searchPlaceholder: string,
    optionLabels?: Record<string, string>,
  ) => (
    <MultiSelectDropdown
      options={[...options]}
      selectedValues={localTradesFilters[filterKey] as string[]}
      onChange={(values) => updateFilter(filterKey, values)}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      pinSelectedToTop={true}
      showClearAll={false}
      optionLabels={optionLabels}
    />
  );

  const renderDateRange = (
    fromKey: keyof typeof localTradesFilters,
    toKey: keyof typeof localTradesFilters,
  ) => (
    <div className="grid grid-cols-1 gap-2">
      <div>
        <label className="block text-xs text-gray-500 mb-1">From</label>
        <input
          type="date"
          max={(localTradesFilters[toKey] as string) || undefined}
          value={(localTradesFilters[fromKey] as string) || ''}
          onChange={(e) => {
            const val = e.target.value || null;
            const toVal = localTradesFilters[toKey] as string | null;
            if (val && toVal && val > toVal) {
              updateFilter(fromKey, toVal);
            } else {
              updateFilter(fromKey, val);
            }
          }}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">To</label>
        <input
          type="date"
          min={(localTradesFilters[fromKey] as string) || undefined}
          value={(localTradesFilters[toKey] as string) || ''}
          onChange={(e) => {
            const val = e.target.value || null;
            const fromVal = localTradesFilters[fromKey] as string | null;
            if (val && fromVal && val < fromVal) {
              updateFilter(toKey, fromVal);
            } else {
              updateFilter(toKey, val);
            }
          }}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
    </div>
  );

  const renderNumericRange = (
    minKey: keyof typeof localTradesFilters,
    maxKey: keyof typeof localTradesFilters,
    step: string,
    minPlaceholder: string,
    maxPlaceholder: string,
  ) => (
    <NumericRangeFilter
      min={localTradesFilters[minKey] as number | null}
      max={localTradesFilters[maxKey] as number | null}
      onChange={(min, max) => setLocalTradesFilters({ ...localTradesFilters, [minKey]: min, [maxKey]: max })}
      step={step}
      minPlaceholder={minPlaceholder}
      maxPlaceholder={maxPlaceholder}
    />
  );

  const renderFilterContent = (filterId: string) => {
    switch (filterId) {
      case 'statuses':
        return renderMultiSelect(filterId, 'statuses', statusOptions, 'Select statuses...', 'Search statuses...', TRADE_STATUS_LABELS);
      case 'counterparties':
        return (
          <CounterpartyFilter
            selected={localTradesFilters.counterparties}
            onChange={(values) => updateFilter('counterparties', values)}
          />
        );
      case 'symbols':
        return renderMultiSelect(filterId, 'symbols', securityOptions, 'Select securities...', 'Search securities...');
      case 'tradeTypes':
        return renderMultiSelect(filterId, 'tradeTypes', MOCK_TRADE_TYPES, 'Select trade types...', 'Search trade types...');
      case 'collateralTypes':
        return renderMultiSelect(filterId, 'collateralTypes', MOCK_COLLATERAL_TYPES, 'Select collateral types...', 'Search collateral types...', COLLATERAL_TYPE_LABELS);
      case 'agreementTypes':
        return renderMultiSelect(filterId, 'agreementTypes', MOCK_AGREEMENT_TYPES, 'Select agreement types...', 'Search agreement types...');
      case 'termTypes':
        return renderMultiSelect(filterId, 'termTypes', MOCK_TERM_TYPES, 'Select trade terms...', 'Search trade terms...');
      case 'currencies':
        return renderMultiSelect(filterId, 'currencies', currencyOptions, 'Select currencies...', 'Search currencies...');
      case 'tradeDate':
        return renderDateRange('tradeDateFrom', 'tradeDateTo');
      case 'settlementDate':
        return renderDateRange('settlementDateFrom', 'settlementDateTo');
      case 'maturityDate':
        return renderDateRange('maturityDateFrom', 'maturityDateTo');
      case 'quantity':
        return renderNumericRange('quantityMin', 'quantityMax', '1', '0', '1000000');
      case 'contractValue':
        return renderNumericRange('contractValueMin', 'contractValueMax', '0.01', '0', '10000000');
      case 'margin':
        return renderNumericRange('marginMin', 'marginMax', '0.01', '1', '199');
      case 'grossPrice':
        return renderNumericRange('grossPriceMin', 'grossPriceMax', '0.01', '0', '10000000');
      case 'dailyEarnings':
        return renderNumericRange('dailyEarningsMin', 'dailyEarningsMax', '0.01', '0', '1000000');
      case 'createdAt':
        return renderDateRange('createdAtFrom', 'createdAtTo');
      case 'updatedAt':
        return renderDateRange('updatedAtFrom', 'updatedAtTo');
      default:
        return null;
    }
  };

  const hasActiveFilters = activeFilterCount > 0;

  // LR-671: the saved-views selector takes the header row (next to the collapse
  // chevron); the Filters / Columns tabs sit right below it — the same layout
  // as the Inventory sidebar header. Saved views are columns / pins / sidebar
  // filters / sorting; the counterparty tabs above the table stay independent.
  const headerContent = (
    <div className="flex-1 min-w-0 ml-4" data-tour="trades-saved-views">
      <SavedViewSelector
        controller={tradesSavedViewsController}
        hooks={getTradesSavedViewsHooks()}
        currentUserEmail={currentUserEmail}
        onActiveViewChange={onActiveViewChange}
      />
    </div>
  );

  const sidebarTabs = (
    <div className="flex items-center gap-1 -mt-3 shrink-0">
      <button
        type="button"
        onClick={() => handleTabChange('filters')}
        className={clsx(
          'px-2.5 py-1 text-base font-semibold transition-colors',
          activeTab === 'filters'
            ? 'text-gray-900 border-b-2 border-blue-600'
            : 'text-gray-500 hover:text-gray-700'
        )}
      >
        Filters
      </button>
      <button
        type="button"
        onClick={() => handleTabChange('columns')}
        className={clsx(
          'px-2.5 py-1 text-base font-semibold transition-colors',
          activeTab === 'columns'
            ? 'text-gray-900 border-b-2 border-blue-600'
            : 'text-gray-500 hover:text-gray-700'
        )}
      >
        Columns
      </button>
    </div>
  );

  return (
    <BaseFilterSidebar
      activeFilterCount={activeFilterCount}
      onClearAll={clearAllFilters}
      headerContent={headerContent}
    >
      {/* Tabs: Filters / Columns — below the saved-views header */}
      {!filterSidebarCollapsed && sidebarTabs}

      {/* Columns tab content */}
      {!filterSidebarCollapsed && activeTab === 'columns' && (
        <div className="flex flex-1 flex-col -mt-2 min-h-0">
          <TradesColumnManagerPanel />
        </div>
      )}

      {/* Filters tab content */}
      {!filterSidebarCollapsed && activeTab === 'filters' && (
        <>
          {hasActiveFilters && (
            <div className="flex justify-start -my-2 -ml-2.5 -mt-4">
              <button
                onClick={clearAllFilters}
                className="text-xs font-medium rounded px-2 py-1 transition-colors text-red-600 hover:text-red-800"
              >
                Clear All
              </button>
            </div>
          )}
          {(() => {
              const collapsibleIds = FILTER_DEFS.map((f) => f.id);
              const allExpanded = FILTER_DEFS.every(
                (f) => expandedFilterIds.includes(f.id) || hasActiveFilter(f.id)
              );
              return (
                <div className="flex items-center justify-between -mt-2 mb-1">
                  <button
                    type="button"
                    onClick={() => {
                      if (allExpanded) {
                        collapseAllFilters();
                      } else {
                        expandAllFilters(collapsibleIds);
                      }
                    }}
                    className="text-xs font-medium text-blue-600 hover:text-blue-800"
                  >
                    {allExpanded ? 'Collapse all' : 'Expand all'}
                  </button>
                </div>
              );
            })()}
          {sortFiltersByColumnOrder(FILTER_DEFS, columnConfig).map((filter) => {
            const active = hasActiveFilter(filter.id);
            const expanded = expandedFilterIds.includes(filter.id) || active;
            const content = renderFilterContent(filter.id);
            if (!content) return null;

            const columnId = TRADES_FILTER_ID_TO_COLUMN_ID[filter.id] ?? null;
            const isColumnVisible = columnId
              ? columnConfig.some((c) => c.id === columnId && c.visible)
              : false;

            return (
              <CollapsibleFilterSection
                key={filter.id}
                filterId={filter.id}
                label={filter.label}
                expanded={expanded}
                onToggle={() => (expanded && !active ? collapseFilter(filter.id) : expandFilter(filter.id))}
                isFocused={focusedFilterId === filter.id}
                hasActiveValues={active}
                columnId={columnId}
                onLocateColumn={(colId) => setLocateColumnRequest({ columnId: colId, trigger: Date.now() })}
                isColumnVisible={isColumnVisible}
              >
                {content}
              </CollapsibleFilterSection>
            );
          })}
          <div>
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {isExporting ? 'Exporting…' : 'Export as CSV'}
            </button>
            {isExporting && (
              <ProgressBar
                className="mt-2"
                value={exportProgress && exportProgress.total > 0 ? exportProgress.done / exportProgress.total : null}
                label="Trades export progress"
                valueText={exportProgressText ?? undefined}
              />
            )}
            <p className="mt-2 text-xs text-gray-500 text-center">
              {isExporting
                ? (exportProgressText ?? 'Fetching the full book…')
                : exportCountUnknown
                  ? 'Filtered trades will be exported'
                  : `${exportRowCount.toLocaleString('en-US')} row${exportRowCount !== 1 ? 's' : ''} will be exported`}
            </p>
          </div>
        </>
      )}
    </BaseFilterSidebar>
  );
};
